import { useState, useMemo, useCallback } from "react";

interface UsePaginationOptions<T> {
  items: T[];
  itemsPerPage?: number;
}

export function usePagination<T>({
  items,
  itemsPerPage = 5,
}: UsePaginationOptions<T>) {
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(items.length / itemsPerPage);

  const displayedItems = useMemo(
    () =>
      items.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage),
    [items, currentPage, itemsPerPage]
  );

  const handlePrevious = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  const resetPage = useCallback(() => setCurrentPage(0), []);

  return {
    currentPage,
    totalPages,
    displayedItems,
    handlePrevious,
    handleNext,
    resetPage,
  };
}
